import { useEffect, useState } from 'react';
import { analyticsApi } from '../../api/admin.js';

const OPS = ['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'in', 'like'];
const FNS = ['count', 'sum', 'avg', 'min', 'max'];

export default function ReportBuilder() {
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState('');
  const [fields, setFields] = useState([]);
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [aggregates, setAggregates] = useState([]);
  const [orderBy, setOrderBy] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    analyticsApi.reportSources().then((d) => {
      setSources(d?.items || []);
      if (d?.items?.[0]) {
        setSource(d.items[0].source);
        setFields(d.items[0].fields.slice(0, 5));
      }
    });
  }, []);

  const def = sources.find((s) => s.source === source);

  function toggleField(f) {
    setFields((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);
  }
  function addFilter() { setFilters([...filters, { field: def?.fields[0] || '', op: 'eq', value: '' }]); }
  function setFilter(i, patch) { setFilters(filters.map((f, j) => j === i ? { ...f, ...patch } : f)); }
  function removeFilter(i) { setFilters(filters.filter((_, j) => j !== i)); }
  function addAggregate() { setAggregates([...aggregates, { fn: 'count', field: def?.fields[0] || '' }]); }
  function addOrderBy() { setOrderBy([...orderBy, { field: def?.fields[0] || '', dir: 'asc' }]); }

  async function run() {
    setError(null);
    try {
      const payload = { source, fields, filters, groupBy, aggregates, orderBy, limit: 500 };
      const r = await analyticsApi.runReport(payload);
      setResult(r);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  async function exportCsv() {
    try {
      const blob = await analyticsApi.runReportCsv({ source, fields, filters, groupBy, aggregates, orderBy, limit: 5000 });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${source}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section>
      <header className="page-header"><h1>Report Builder</h1></header>
      {error && <div className="alert error">{error}</div>}

      <div className="grid-2">
        <div className="card">
          <h3>Configure</h3>
          <div className="grid-form">
            <label className="full">Source
              <select value={source} onChange={(e) => { setSource(e.target.value); setFields([]); setFilters([]); setGroupBy([]); setAggregates([]); setOrderBy([]); }}>
                {sources.map((s) => <option key={s.source} value={s.source}>{s.source}</option>)}
              </select>
            </label>
          </div>
          <h4>Fields</h4>
          <div className="vendor-grid">
            {(def?.fields || []).map((f) => (
              <label key={f} className={`vendor-pick ${fields.includes(f) ? 'picked' : ''}`}>
                <input type="checkbox" checked={fields.includes(f)} onChange={() => toggleField(f)} /> {f}
              </label>
            ))}
          </div>

          <h4>Filters</h4>
          {filters.map((f, i) => (
            <div key={i} className="cond-row">
              <span className="muted small">WHERE</span>
              <select value={f.field} onChange={(e) => setFilter(i, { field: e.target.value })}>
                {(def?.fields || []).map((x) => <option key={x}>{x}</option>)}
              </select>
              <select value={f.op} onChange={(e) => setFilter(i, { op: e.target.value })}>
                {OPS.map((x) => <option key={x}>{x}</option>)}
              </select>
              <input value={f.value} onChange={(e) => setFilter(i, { value: e.target.value })} />
              <button className="btn ghost small" type="button" onClick={() => removeFilter(i)}>×</button>
            </div>
          ))}
          <button className="btn ghost small" type="button" onClick={addFilter}>+ Filter</button>

          <h4>Group by</h4>
          <select multiple value={groupBy} onChange={(e) => setGroupBy([...e.target.selectedOptions].map((o) => o.value))} style={{ minHeight: 80, width: '100%' }}>
            {(def?.fields || []).map((x) => <option key={x}>{x}</option>)}
          </select>

          <h4>Aggregates</h4>
          {aggregates.map((a, i) => (
            <div key={i} className="cond-row">
              <span className="muted small">AGG</span>
              <select value={a.fn} onChange={(e) => setAggregates(aggregates.map((x, j) => j === i ? { ...x, fn: e.target.value } : x))}>
                {FNS.map((x) => <option key={x}>{x}</option>)}
              </select>
              <select value={a.field} onChange={(e) => setAggregates(aggregates.map((x, j) => j === i ? { ...x, field: e.target.value } : x))}>
                {(def?.fields || []).map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
          ))}
          <button className="btn ghost small" type="button" onClick={addAggregate}>+ Aggregate</button>

          <h4>Order by</h4>
          {orderBy.map((o, i) => (
            <div key={i} className="cond-row">
              <span className="muted small">ORDER</span>
              <select value={o.field} onChange={(e) => setOrderBy(orderBy.map((x, j) => j === i ? { ...x, field: e.target.value } : x))}>
                {(def?.fields || []).map((x) => <option key={x}>{x}</option>)}
              </select>
              <select value={o.dir} onChange={(e) => setOrderBy(orderBy.map((x, j) => j === i ? { ...x, dir: e.target.value } : x))}>
                <option>asc</option><option>desc</option>
              </select>
            </div>
          ))}
          <button className="btn ghost small" type="button" onClick={addOrderBy}>+ Order</button>

          <div className="actions" style={{ marginTop: 12 }}>
            <button className="btn primary" type="button" onClick={run}>Run report</button>
            <button className="btn ghost" type="button" onClick={exportCsv}>Export CSV</button>
          </div>
        </div>

        <div className="card">
          <h3>Result {result?.rows?.length != null && <span className="muted small">({result.rows.length} rows)</span>}</h3>
          {!result ? <div className="muted">Run a report to see preview.</div> : (
            <table className="data-table">
              <thead><tr>{result.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i}>{result.columns.map((c) => <td key={c}>{String(r[c] ?? '—').slice(0, 80)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}
